import React from 'react';
import { MapPin, Copy, Check } from 'lucide-react';

interface AddressProps {
  type: 'billing' | 'shipping';
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  companyName?: string | null;
  vatNumber?: string | null;
}

interface OrderAddressesProps {
  billingAddress?: AddressProps;
  shippingAddress?: AddressProps;
  invoiceEmail?: string | null;
}

const AddressCard: React.FC<AddressProps & { onCopy?: () => void; copied?: boolean }> = ({
  type,
  address,
  city,
  postalCode,
  country,
  companyName,
  vatNumber,
  onCopy,
  copied
}) => {
  const isBilling = type === 'billing';
  const hasAddress = address || city || postalCode;

  return (
    <div
      className={`p-4 rounded-xl border-2 ${
        isBilling
          ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
          : 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isBilling
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
            }`}
          >
            {isBilling ? <MapPin className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
          </div>
          <h4
            className={`font-semibold text-sm ${
              isBilling
                ? 'text-blue-700 dark:text-blue-300'
                : 'text-emerald-700 dark:text-emerald-300'
            }`}
          >
            {isBilling ? 'Facturation' : 'Livraison'}
          </h4>
        </div>

        {hasAddress && onCopy && (
          <button
            onClick={onCopy}
            className={`p-1.5 rounded-lg transition-colors ${
              copied
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600'
                : 'hover:bg-slate-200/50 dark:hover:bg-slate-700/50 text-slate-400'
            }`}
            title="Copier l'adresse"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {hasAddress ? (
        <div className="space-y-1 text-sm text-slate-700 dark:text-slate-300">
          {companyName && <p className="font-medium">{companyName}</p>}
          {address && <p>{address}</p>}
          {(postalCode || city) && (
            <p>
              {postalCode} {city}
            </p>
          )}
          {country && <p>{country}</p>}
          {vatNumber && (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              TVA: {vatNumber}
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500 italic">
          Adresse non renseignée
        </p>
      )}
    </div>
  );
};

const OrderAddresses: React.FC<OrderAddressesProps> = ({
  shippingAddress,
  invoiceEmail
}) => {
  const [copiedShipping, setCopiedShipping] = React.useState(false);

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedShipping(true);
      setTimeout(() => setCopiedShipping(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatAddressForCopy = (addr?: AddressProps): string => {
    if (!addr) return '';
    return [
      addr.companyName,
      addr.address,
      `${addr.postalCode || ''} ${addr.city || ''}`.trim(),
      addr.country
    ]
      .filter(Boolean)
      .join('\n');
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AddressCard
          type="shipping"
          {...shippingAddress}
          onCopy={() => copyToClipboard(formatAddressForCopy(shippingAddress))}
          copied={copiedShipping}
        />
      </div>

      {invoiceEmail && (
        <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
            Email de facturation
          </p>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {invoiceEmail}
          </p>
        </div>
      )}
    </div>
  );
};

export default OrderAddresses;
